import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { filter } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class NavigationHistoryService {
  private _stack: string[] = [];
  private previousUrl: string | null = null;
  private currentUrl: string;

  constructor(private router: Router, private location: Location) {
    this.currentUrl = this.router.url;

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const next = event.urlAfterRedirects;
        // Only update history when the URL actually changes
        if (next !== this.currentUrl) {
          const last = this._stack.length ? this._stack[this._stack.length - 1] : null;
          if (last === next) {
            this._stack.pop();
          } else if (!this.isAuthRoute(this.currentUrl)) {
            this._stack.push(this.currentUrl);
          }
          if (!this.isAuthRoute(this.currentUrl)) {
            this.previousUrl = this.currentUrl;
          }
          this.currentUrl = next;
        }
      });
  }

  private isAuthRoute(url: string): boolean {
    return !url || url === '/' || url.startsWith('/account/');
  }

  /** Returns the URL the user came from, or null if this is the first page. */
  getPreviousUrl(): string | null {
    return this.previousUrl;
  }

  /**
   * Navigates back one step following the user's actual navigation history
   * (like a standard browser Back action), preserving previous route state such
   * as query parameters, filters and pagination.
   *
   * If there is no in-app history to go back to (e.g., a direct/deep page load),
   * navigates to `fallback` instead.
   *
   * @param fallback - Route segments or a URL string to use when no history exists.
   *                   Pass an array for `router.navigate()` or a string for `navigateByUrl()`.
   */
  goBack(fallback: string | string[] = '/dashboard'): void {
    if (this._stack.length > 0) {
      this.location.back();
    } else if (Array.isArray(fallback)) {
      this.router.navigate(fallback);
    } else {
      this.router.navigateByUrl(fallback);
    }
  }
}
